import { useEffect, useMemo, useState, type ReactNode } from "react";
import { VestigingFilter } from "../components/VestigingFilter";
import { useVestigingKeuze, type VestigingKeuze } from "../components/useVestigingKeuze";
import {
  CatalogusLijst, SoortTegels, WeergaveKeuze, soortTegelsUitRijen,
  type Weergave,
} from "../components/CatalogusLijst";
import { leesWeergave, bewaarWeergave } from "../components/CatalogusKop";
import { AiHulpKnop, AiTariefHulp, useAiBeschikbaar, type TariefSeed } from "../components/AiTariefHulp";
import { speciesIconSrc } from "../components/AnimalOverview";
import { TablePager } from "../components/TablePager";
import { BtwKeuze, btwWeergave, huidigeBtwUitHistorie, useBtwCodes } from "../components/BtwKeuze";
import {
  BedDouble, ChevronDown, ChevronUp, GripVertical, Home, Lock, LockOpen, PawPrint,
  Power as PowerIcon, PowerOff as PowerOffIcon, Snowflake, Sun,
} from "lucide-react";
import { OverviewHeader } from "../components/OverviewHeader";
import { PensionReserveringsproducten } from "./settings/PensionReserveringsproducten";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import {
  ChevronRightIcon,
  TrashIcon,
  PencilIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  TextareaField,
  type Column,
  type StatusTone,
  PlusIcon,
} from "@fawkes/ui";

import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { AccommodatieDagprijzen } from "./pensiontarief/AccommodatieDagprijzen";
import { AccommodatieTarievenTab } from "./pensiontarief/AccommodatieTarievenTab";
import type { Prijsberekening } from "./pensionplanning/accommodatie";
import { keuzes, labelVan, useFinancieleOpties } from "./pensiontarief/financieleOpties";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// verblijf-wizard-tarief Epic 3 (Story 3.1–3.3): the Pension tarief overview — the React port of the
// Jmix `PensionTariefOverviewView`. Three top tabs (Tarieven / Hoogseizoen / Kortingen):
//   • Tarieven — per-day laag/hoog day-prices grid + KPI cards + a high-season banner; add opens
//     NieuwPensionTariefDialog; edit opens TariefBewerkDialog (the 4-section full editor:
//     Basisinformatie / Huidig tarief / Prijswijziging inplannen / Tariefoverzicht).
//   • Hoogseizoen — closed-interval [geldigVanaf, geldigTm] periods; inline create + soft-delete.
//   • Kortingen — ALS/EN/DAN/OP discount-rule builder (kortingstype + waarde + toepassing); AI button
//     opens AiKortingRegelDialog. CRUD only — no evaluation, exactly like the Jmix service.
//
// All endpoints use RELATIVE urls (the dataProvider prepends /api once; a leading slash doubles to
// /api/api). ROLE_TARIEF_EDIT is server-enforced — there is no client signal, so affordances stay
// enabled and a 403 surfaces inline. A back-dated prijswijziging needs ROLE_TARIEF_ADMIN server-side
// → its 403 surfaces with a distinct "you need admin rights for a past date" message. The Kortingen
// "AI" button is hidden when /kortingen/ai-suggestie 404s (the `fawkes.producten.ai-features.enabled`
// flag is off → the route does not exist).

// ── enum vocabularies (stable wire ids, matching the Java enums) ──────────────────────────────────
const KORTING_TYPES = ["PERCENTAGE", "VAST_BEDRAG"] as const;
const TOEPASSINGEN = ["ALLEEN_EXTRA_DIEREN", "ALLE_DIEREN", "LANGSTE_VERBLIJF"] as const;

// ── wire DTOs ──────────────────────────────────────────────────────────────────────────────────────
interface PensionTariefRow {
  tariefId: number;
  version: number | null;
  vestigingId: number | null;
  diersoortId: number | null;
  diersoortNaam: string;
  verblijftype: string;
  omschrijving: string | null;
  dagprijs: number | null;
  dagprijsHoog: number | null;
  effectieveDagprijs: number | null;
  geldigVanaf: string | null;
  laatstGewijzigd: string | null;
  isNieuw: boolean;
  isHoogseizoenOpPeildatum: boolean;
  /** Hoeveel rassen onder dit tarief vallen. */
  aantalRassen: number;
  /** Of dit tarief nog gekozen kan worden; een inactief tarief blijft staan voor de historie. */
  actief: boolean;
  /** De plek in de lijst zoals het pension hem zelf sorteert. */
  positie: number;
}

interface PensionTariefKpis {
  actieveTarieven: number;
  aankomendeWijzigingen: number;
  ontbrekendeSeizoensprijzen: number;
  hoogseizoenActief: boolean;
}

interface TariefHistorieRegel {
  versieId: number;
  bedrag: number;
  bedragHoog: number | null;
  effectdatum: string;
  einddatum: string | null;
  reden: string | null;
  btwCodeId: number | null;
  /** Waar deze prijs op geboekt werd; leeg bij versies van voor deze velden bestonden. */
  grootboekRekeningId: number | null;
  kostenPlaatsId: number | null;
  projectCodeId: number | null;
  status: "GEPLAND" | "ACTIEF" | "VERVALLEN";
}

interface HoogseizoenPeriode {
  id: number;
  version: number;
  naam: string;
  vestigingId: number | null;
  geldigVanaf: string;
  geldigTm: string;
  actief: boolean;
}

interface KortingRegel {
  id: number;
  version: number;
  naam: string;
  vestigingId: number | null;
  conditieJson: string;
  kortingstype: string | null;
  waarde: number;
  toepassing: string | null;
  geldigVanaf: string;
  geldigTm: string | null;
  actief: boolean;
}

interface KortingAiDraft {
  suggestieNaam: string;
  conditieJson: string;
  kortingstype: string;
  waarde: number;
  toepassing: string;
  aantalDieren: number | null;
  diersoort: string | null;
  verblijfsduurDagen: number | null;
  bronAiTekst: string;
  previewTekst: string;
}

// ── helpers ──────────────────────────────────────────────────────────────────────────────────────
type SaveNotice = "conflict" | "forbidden" | "forbiddenPast" | "error" | null;
const statusToErr = (err: unknown, isPast = false): SaveNotice => {
  const code = (err as { statusCode?: number }).statusCode;
  if (code === 409) return "conflict";
  if (code === 403) return isPast ? "forbiddenPast" : "forbidden";
  return "error";
};
const num = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

function historieTone(status: TariefHistorieRegel["status"]): StatusTone {
  if (status === "ACTIEF") return "success";
  if (status === "GEPLAND") return "warning";
  return "neutral";
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────
type Tab = "prijsberekening" | "hoogseizoen" | "kortingen" | "aanbetaling" | "verzekering";

export function PensionTariefOverview() {
  const t = useTranslate();
  const [tab, setTab] = useState<Tab>("prijsberekening");
  // Het bereik waarover je kijkt: alles, alleen wat organisatiebreed geldt, of alleen wat een
  // vestiging zelf heeft ingesteld. Client-side, want de rijen dragen hun vestiging al mee.
  // Dezelfde vestigingkeuze als op Producten & diensten en het factuuroverzicht.
  const vestiging = useVestigingKeuze();
  const prijsmodel = usePrijsmodel(vestiging);
  // Hoogseizoen gaat over wannéér de hoge dagprijs van een tarieftype geldt. Rekent de vestiging per
  // accommodatie, dan bestaat die prijs niet en valt er niets af te bakenen; dan hoort het tabblad er
  // ook niet te staan.
  const perDier = prijsmodel.model === "PENSIONPRIJZEN";

  const tabs: { key: Tab; label: string }[] = [
    { key: "prijsberekening", label: t("pensionTarief.tab.prijsberekening") },
    ...(perDier ? [{ key: "hoogseizoen" as Tab, label: t("pensionTarief.tab.hoogseizoen") }] : []),
    { key: "kortingen", label: t("pensionTarief.tab.kortingen") },
    { key: "aanbetaling", label: t("pensionTarief.tab.aanbetaling") },
    { key: "verzekering", label: t("pensionTarief.tab.verzekering") },
  ];

  // Wie op Hoogseizoen staat en overschakelt naar dagprijzen per accommodatie, staat op een tabblad
  // dat er niet meer is; dan terug naar het tabblad waar hij zojuist iets veranderde.
  useEffect(() => {
    if (tab === "hoogseizoen" && !perDier) {
      setTab("prijsberekening");
    }
  }, [tab, perDier]);

  return (
    <div className="product-overview pension-tarief">
      {/* acc heads the screen with its title and puts the tab strip under it (same as the catalogus).
          Without it the page opened on a bare tab bar with no idea what it belonged to. */}
      <OverviewHeader
        icon={<BedDouble size={24} />}
        title={t("menu.pensionTarief")}
        subtitle={t("pensionTarief.subtitel")}
      />

      <div className="product-tabs" role="tablist" aria-label={t("pensionTarief.tabs.label")}>
        {tabs.map((tabDef) => (
          <button
            key={tabDef.key}
            type="button"
            role="tab"
            aria-selected={tab === tabDef.key}
            className={`product-tab${tab === tabDef.key ? " product-tab--active" : ""}`}
            onClick={() => setTab(tabDef.key)}
          >
            {tabDef.label}
          </button>
        ))}
      </div>

      {tab === "prijsberekening" && <PrijsberekeningTab vestiging={vestiging} prijsmodel={prijsmodel} />}
      {tab === "hoogseizoen" && <HoogseizoenTab />}
      {tab === "kortingen" && <KortingenTab />}
      {tab === "aanbetaling" && <AanbetalingTab />}
      {tab === "verzekering" && <PensionReserveringsproducten />}
    </div>
  );
}

interface RasRegel {
  rasId: number;
  naam: string;
  /** Het tarief waar dit ras nu bij hoort; leeg als het nergens bij hoort of al bij dit tarief. */
  elders: string | null;
}

/**
 * Welke rassen onder dit tarief vallen.
 *
 * <p>De koppeling zit als kolom op het ras, dus een ras hoort bij <b>hoogstens één</b> pensiontarief.
 * Een ras hier aanvinken haalt het weg bij het tarief waar het nu bij hoort, en dat staat er bij het ras
 * bij — stilletjes verplaatsen zou betekenen dat een ander tarief ineens minder dieren dekt zonder dat
 * iemand het ziet.
 */
function RassenVenster({
  tariefId, tariefNaam, onSluit, onOpgeslagen,
}: {
  tariefId: number;
  tariefNaam: string;
  onSluit: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { data, isLoading } = useCustom<{ gekozen: RasRegel[]; overige: RasRegel[] }>({
    url: `pension-tarief/${tariefId}/rassen`,
    method: "get",
  });
  const { mutate: bewaar, isLoading: bezig } = useCustomMutation();
  const [zoek, setZoek] = useState("");
  const [gekozen, setGekozen] = useState<Set<number> | null>(null);
  const [fout, setFout] = useState(false);

  const alle = useMemo(() => {
    const d = data?.data;
    return d ? [...d.gekozen, ...d.overige].sort((a, b) => a.naam.localeCompare(b.naam, "nl")) : [];
  }, [data]);
  // De aangevinkte set komt van de server tot er iets is aangeklikt; daarna is de bewerking leidend.
  const aangevinkt = gekozen ?? new Set((data?.data?.gekozen ?? []).map((r) => r.rasId));

  const zichtbaar = alle.filter((r) => r.naam.toLowerCase().includes(zoek.trim().toLowerCase()));

  const wissel = (rasId: number) => {
    const volgende = new Set(aangevinkt);
    if (volgende.has(rasId)) {
      volgende.delete(rasId);
    } else {
      volgende.add(rasId);
    }
    setGekozen(volgende);
  };

  return (
    <Modal open title={t("pensionTarief.rassen.titel", { args: [tariefNaam] })} onClose={onSluit}
      footer={
        <div className="pt-venstervoet">
          <span className="pt-rassen__telling">
            {t("pensionTarief.rassen.gekozen", { args: [String(aangevinkt.size)] })}
          </span>
          {fout ? <span className="pt-venstervoet__fout" role="alert">{t("pensionTarief.rassen.fout")}</span> : null}
          <span className="pt-venstervoet__vulling" />
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button
            disabled={bezig}
            onClick={() => {
              setFout(false);
              bewaar(
                {
                  url: `pension-tarief/${tariefId}/rassen`,
                  method: "put",
                  values: { rasIds: [...aangevinkt] },
                  successNotification: false,
                  errorNotification: false,
                },
                { onSuccess: onOpgeslagen, onError: () => setFout(true) },
              );
            }}
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <div className="pt-rassen">
        <Field
          label={t("pensionTarief.rassen.zoek")}
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
        />
        {isLoading ? <p className="pt-rassen__leeg">{t("common.loading")}</p> : null}
        {!isLoading && alle.length === 0 ? (
          <p className="pt-rassen__leeg">{t("pensionTarief.rassen.geen")}</p>
        ) : null}
        <ul className="pt-rassen__lijst">
          {zichtbaar.map((r) => (
            <li key={r.rasId} className="pt-rassen__regel">
              <label className="pt-rassen__keuze">
                <input
                  type="checkbox"
                  checked={aangevinkt.has(r.rasId)}
                  onChange={() => wissel(r.rasId)}
                  data-testid={`ras-${r.rasId}`}
                />
                <span className="pt-rassen__naam">{r.naam}</span>
              </label>
              {r.elders ? (
                <span className="pt-rassen__elders">
                  {t("pensionTarief.rassen.elders", { args: [r.elders] })}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

// ── Prijsberekening ──────────────────────────────────────────────────────────────────────────────

/**
 * Het prijsmodel van de vestiging, en daaronder de tarieven die erbij horen.
 *
 * <p>Hiervoor waren dit drie tabbladen — Tarieven, Accommodaties, Hoogseizoen — die elk maar de helft
 * van het verhaal vertelden. Welk van de twee modellen deze vestiging voert stond er nergens bij,
 * terwijl dát bepaalt welke tarieven meetellen bij een reservering: bij dagprijzen per dier zijn de
 * accommodatieprijzen een toeslag, bij dagprijzen per accommodatie bepaalt de ruimte de prijs. Wie de
 * verkeerde tab invulde, vulde iets in dat nergens op sloeg.
 *
 * <p>De keuze zelf is niet nieuw: hij staat al op de vestiging ({@code pension/prijsberekening}) en was
 * te vinden onder Instellingen › Pension. Hier staat hij bovenaan, bij de tarieven waar hij over gaat.
 */
interface Prijsmodel {
  model: Prijsberekening;
  kies: (nieuw: Prijsberekening) => void;
  magBeheren: boolean;
  fout: boolean;
}

/**
 * Het prijsmodel van de vestiging: lezen en zetten.
 *
 * <p>Staat apart omdat twee plekken hem nodig hebben — de keuzekaarten op Prijsberekening, en de
 * tabstrip die er het tabblad Hoogseizoen mee toont of weglaat. Zouden die allebei hun eigen kopie
 * bijhouden, dan verspringt de keuze wel maar de tabstrip niet.
 */
function usePrijsmodel(vestiging: VestigingKeuze): Prijsmodel {
  // Het prijsmodel staat op één vestiging, terwijl de filter er meerdere toelaat. Alleen bij precies
  // één aangevinkte vestiging is duidelijk welke bedoeld wordt; anders geldt wat er organisatiebreed
  // staat.
  const vestigingId = vestiging.gekozenIds.size === 1 ? [...vestiging.gekozenIds][0] : null;
  const query = vestigingId == null ? "" : `?vestigingId=${vestigingId}`;
  // Wijzigen is beheer (ROLE_PENSION_ADMIN, net als de api); zonder dat recht is dit een overzicht.
  const { data: rechten } = usePermissions<string[]>();
  const magBeheren = (rechten ?? []).includes("ROLE_PENSION_ADMIN");

  const instelling = useCustom<{ prijsberekening: Prijsberekening }>({
    url: `pension/prijsberekening${query}`,
    method: "get",
  });
  const { mutate: bewaar } = useCustomMutation();
  const [fout, setFout] = useState(false);

  // Wat de server zegt, tenzij er net geklikt is: de radioknop hoort meteen te reageren en niet pas
  // als het antwoord terug is. Mislukt het bewaren, dan springt hij terug en staat de melding erbij.
  const [gekozen, setGekozen] = useState<Prijsberekening | null>(null);
  const model: Prijsberekening =
    gekozen ?? instelling.data?.data?.prijsberekening ?? "PENSIONPRIJZEN";

  const kies = (nieuw: Prijsberekening) => {
    if (nieuw === model || !magBeheren) return;
    const vorig = model;
    setGekozen(nieuw);
    setFout(false);
    bewaar(
      {
        url: `pension/prijsberekening${query}`,
        method: "put",
        values: { prijsberekening: nieuw },
      },
      {
        onError: () => {
          setGekozen(vorig);
          setFout(true);
        },
        onSuccess: () => instelling.refetch?.(),
      },
    );
  };

  return { model, kies, magBeheren, fout };
}

function PrijsberekeningTab({ vestiging, prijsmodel }: { vestiging: VestigingKeuze; prijsmodel: Prijsmodel }) {
  const t = useTranslate();
  const { model, kies, magBeheren, fout } = prijsmodel;
  /**
   * Of de keuze open staat.
   *
   * <p>Deze knop bepaalt welke tarieven een reservering rekent: bij dagprijzen per dier het tarief van
   * het dier met de accommodatie als toeslag, bij dagprijzen per accommodatie de ruimte zelf. Omzetten
   * laat bestaande reserveringen met rust maar verandert alles wat erna komt, en dat is te zwaar voor
   * iets wat je aanraakt terwijl je iets anders zocht. Het slot valt na een wijziging weer dicht.
   */
  const [open, setOpen] = useState(false);
  const magKiezen = magBeheren && open;

  const ontgrendel = () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (globalThis.confirm(t("pensionTarief.prijsmodel.slotVraag"))) {
      setOpen(true);
    }
  };

  const opties: { waarde: Prijsberekening; icoon: ReactNode; naam: string; uitleg: string }[] = [
    {
      waarde: "PENSIONPRIJZEN",
      icoon: <PawPrint size={22} aria-hidden="true" />,
      naam: t("pensionTarief.prijsmodel.dier.naam"),
      uitleg: t("pensionTarief.prijsmodel.dier.uitleg"),
    },
    {
      waarde: "ACCOMMODATIE",
      icoon: <Home size={22} aria-hidden="true" />,
      naam: t("pensionTarief.prijsmodel.accommodatie.naam"),
      uitleg: t("pensionTarief.prijsmodel.accommodatie.uitleg"),
    },
  ];

  return (
    <>
      <section className="pt-prijsmodel" aria-labelledby="pt-prijsmodel-kop">
        <div className="pt-prijsmodel__balk">
          <h2 className="pt-prijsmodel__kop" id="pt-prijsmodel-kop">
            {t("pensionTarief.prijsmodel.titel")}
          </h2>
          {magBeheren ? (
            <button
              type="button"
              className={"pt-prijsmodel__slot" + (open ? " pt-prijsmodel__slot--open" : "")}
              onClick={ontgrendel}
              data-testid="prijsmodel-slot"
              aria-pressed={open}
            >
              {open ? <LockOpen size={15} aria-hidden="true" /> : <Lock size={15} aria-hidden="true" />}
              {t(open ? "pensionTarief.prijsmodel.slotOpen" : "pensionTarief.prijsmodel.slotDicht")}
            </button>
          ) : null}
        </div>
        <div
          className={"pt-prijsmodel__keuzes" + (magKiezen ? "" : " pt-prijsmodel__keuzes--op-slot")}
          role="radiogroup"
          aria-labelledby="pt-prijsmodel-kop"
        >
          {opties.map((o) => (
            <label
              key={o.waarde}
              className={"pt-prijsmodel__kaart" + (model === o.waarde ? " pt-prijsmodel__kaart--aan" : "")}
            >
              <input
                type="radio"
                name="pt-prijsmodel"
                className="pt-prijsmodel__radio"
                data-testid={`prijsmodel-${o.waarde}`}
                checked={model === o.waarde}
                disabled={!magKiezen}
                onChange={() => { kies(o.waarde); setOpen(false); }}
              />
              <span className="pt-prijsmodel__stip" aria-hidden="true" />
              <span className="pt-prijsmodel__icoon" aria-hidden="true">{o.icoon}</span>
              <span className="pt-prijsmodel__tekst">
                <span className="pt-prijsmodel__naam">{o.naam}</span>
                <span className="pt-prijsmodel__uitleg">{o.uitleg}</span>
              </span>
            </label>
          ))}
        </div>
        {fout ? (
          <p className="pt-prijsmodel__fout" role="alert">
            {t("pensionTarief.prijsmodel.opslaanFout")}
          </p>
        ) : null}
        {!magBeheren ? (
          <p className="pt-prijsmodel__fout pt-prijsmodel__fout--zacht">
            {t("pensionTarief.prijsmodel.alleenLezen")}
          </p>
        ) : null}
      </section>

      {model === "ACCOMMODATIE" ? (
        // De standaard dagprijs per type met zijn historie blijft de lijst eronder; de kalender erboven
        // gaat over de afwijkingen per dag. Twee views op dezelfde prijs, en de kalender laat zien welke
        // van de twee een dag volgt.
        <>
          <AccommodatieDagprijzen vestiging={vestiging} />
          <AccommodatieTarievenTab vestiging={vestiging} />
        </>
      ) : (
        <TarievenTab vestiging={vestiging} />
      )}
    </>
  );
}

// ── Tarieven tab ─────────────────────────────────────────────────────────────────────────────────
function TarievenTab({ vestiging }: { vestiging: VestigingKeuze }) {
  // Welk tarief zijn rassen laat beheren; null is dicht.
  const [rassenVan, setRassenVan] = useState<PensionTariefRow | null>(null);
  const { mutate: bewaarVolgorde } = useCustomMutation();
  const [volgordeFout, setVolgordeFout] = useState(false);

  /** Een tarief in of uit gebruik zetten; verwijderen zou de prijshistorie weggooien. */
  const wisselActief = (r: PensionTariefRow) => {
    bewaarVolgorde(
      {
        url: `pension-tarief/${r.tariefId}/basisinfo`,
        method: "put",
        values: {
          naam: r.verblijftype, diersoortId: r.diersoortId, omschrijving: r.omschrijving,
          actief: !r.actief, version: r.version,
        },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => refreshAll(), onError: () => setVolgordeFout(true) },
    );
  };

  /**
   * De nieuwe volgorde vastleggen.
   *
   * <p>De hele reeks id-s gaat mee, niet een nummer per rij: zo kan een rij er niet tussenuit vallen en
   * houden de posities aansluitende nummers.
   */
  const verplaats = (r: PensionTariefRow, richting: -1 | 1) => {
    const lijst = alleRijen;
    const vanPlek = lijst.findIndex((x) => x.tariefId === r.tariefId);
    const naarPlek = vanPlek + richting;
    if (vanPlek < 0 || naarPlek < 0 || naarPlek >= lijst.length) return;
    const nieuw = [...lijst];
    [nieuw[vanPlek], nieuw[naarPlek]] = [nieuw[naarPlek], nieuw[vanPlek]];
    setVolgordeFout(false);
    bewaarVolgorde(
      {
        url: "pension-tarief/volgorde",
        method: "put",
        values: { tariefIds: nieuw.map((r) => r.tariefId) },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => refreshAll(), onError: () => setVolgordeFout(true) },
    );
  };
  const { hoortErbij } = vestiging;
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  // Source parity: the "Geldig op" peildatum filter (the api resolves the heads on that date). Empty =
  // today (the api default), matching the source's initial state.
  const [peildatum, setPeildatum] = useState("");
  const { data, isLoading, isError, refetch } = useCustom<PensionTariefRow[]>({
    url: peildatum ? `pension-tarief?peildatum=${encodeURIComponent(peildatum)}` : "pension-tarief",
    method: "get",
  });
  const { data: kpiData, refetch: refetchKpis } = useCustom<PensionTariefKpis>({
    url: "pension-tarief/kpis",
    method: "get",
    queryOptions: { retry: false },
  });

  const alleRijen = useMemo(() => (Array.isArray(data?.data) ? data!.data : []), [data]);
  const kpis = kpiData?.data;

  // ── client search / sort / paging (Jmix grid behaviour) ──
  const [zoek, setZoek] = useState("");
  // acc sorts by clicking a column header (the two Sorteren/Richting selects it never had are gone).
  // Waar je op dit scherm tussen kiest is de diersoort: pensiontarieven staan per soort dier.
  const [diersoort, setDiersoort] = useState("");
  const [hoogFilter, setHoogFilter] = useState("");
  const [weergave, setWeergave] = useState<Weergave>(() => leesWeergave("fawkes.pensionTarief"));
  const kiesWeergave = (w: Weergave) => {
    setWeergave(w);
    bewaarWeergave("fawkes.pensionTarief", w);
  };
  const [sortKey, setSortKey] = useState<"verblijftype" | "diersoortNaam" | "dagprijs" | "dagprijsHoog">(
    "verblijftype",
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  // Tien is wat er stond; sinds de voetregel een keuzelijst heeft mag je er zelf van afwijken.
  const [pageSize, setPageSize] = useState(10);

  // De tegels tellen binnen de vestigingkeuze maar vóór de soortkeuze: anders zou elke andere tegel
  // op nul springen zodra je er één aanklikt.
  // Diersoort staat in de hele database leeg — daar valt niets op te kiezen. Waar wel verschil in
  // zit is de hoogseizoenprijs, en dat is precies de vraag die de weggehaalde KPI-kaart stelde.
  const hoogseizoenGroep = (r: PensionTariefRow) =>
    r.dagprijsHoog == null || r.dagprijsHoog === 0
      ? t("pensionTarief.hoog.geen")
      : r.dagprijsHoog === r.dagprijs
        ? t("pensionTarief.hoog.gelijk")
        : t("pensionTarief.hoog.eigen");
  const tegels = useMemo(() => soortTegelsUitRijen(
    alleRijen.filter((r) => hoortErbij(r)),
    (r) => r.diersoortNaam,
    t("dierAanwezig.stat.total"),
    (soort) => <img src={speciesIconSrc(soort)} alt="" width={28} height={28} />,
  ), [alleRijen, hoortErbij, t]);

  const filtered = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    // Eerst het bereik, dan pas zoeken: wie op VESTIGING staat wil ook binnen die selectie zoeken.
    const binnenVestiging = alleRijen.filter((r) => hoortErbij(r));
    const opSoort = diersoort ? binnenVestiging.filter((r) => r.diersoortNaam === diersoort) : binnenVestiging;
    const rows = hoogFilter ? opSoort.filter((r) => hoogseizoenGroep(r) === hoogFilter) : opSoort;
    const matched = q
      ? rows.filter(
          (r) =>
            r.verblijftype.toLowerCase().includes(q) ||
            (r.diersoortNaam ?? "").toLowerCase().includes(q) ||
            (r.omschrijving ?? "").toLowerCase().includes(q),
        )
      : rows;
    const sorted = [...matched].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (typeof av === "number" || typeof bv === "number") {
        cmp = (av == null ? -Infinity : (av as number)) - (bv == null ? -Infinity : (bv as number));
      } else {
        cmp = String(av ?? "").localeCompare(String(bv ?? ""));
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [alleRijen, hoortErbij, diersoort, hoogFilter, zoek, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);
  // keep the page in range when the filter shrinks the result set
  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [page, pageCount]);

  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [nieuwSeed, setNieuwSeed] = useState<TariefSeed | undefined>(undefined);
  const [aiOpen, setAiOpen] = useState(false);
  const [bewerkRow, setBewerkRow] = useState<PensionTariefRow | null>(null);
  const diersoorten = useDiersoortOpties();
  const aiBeschikbaar = useAiBeschikbaar("pension-tarief/ai-suggestie");
  const { mutate: del } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const refreshAll = () => {
    refetch();
    refetchKpis();
  };

  const onDelete = (r: PensionTariefRow) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("pensionTarief.delete.confirm", { args: [r.verblijftype] }))) return;
    del(
      {
        url: `pension-tarief/${r.tariefId}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refreshAll(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  // acc's column order: Diersoort · Verblijftype · Laagseizoen · Hoogseizoen · Geldig vanaf ·
  // Laatst gewijzigd · Acties. We led with Verblijftype and left "Laatst gewijzigd" out entirely,
  // so you could not see when a price was last touched without opening the row.
  const columns: Column<PensionTariefRow>[] = [
    {
      header: t("pensionTarief.col.diersoort"),
      sortKey: "diersoortNaam",
      // Als badge, zoals de productenlijst zijn Type en Scope toont — dat is de norm voor een
      // categoriekolom, en het maakt de regels ook even hoog als daar.
      cell: (r) => (r.diersoortNaam ? <StatusBadge tone="neutral">{r.diersoortNaam}</StatusBadge> : "—"),
    },
    {
      header: t("pensionTarief.col.verblijftype"),
      sortKey: "verblijftype",
      cell: (r) => (
        <span className="pension-tarief__nieuw-cell">
          {r.verblijftype}
          {r.isNieuw && <StatusBadge tone="success">{t("pensionTarief.badge.nieuw")}</StatusBadge>}
        </span>
      ),
    },
    {
      header: t("pensionTarief.col.dagprijs"),
      sortKey: "dagprijs",
      cell: (r) => (
        <span className="pension-tarief__prijs">
          {r.dagprijs != null ? formatEuro(r.dagprijs, locale) : t("pensionTarief.none")}
        </span>
      ),
    },
    {
      header: t("pensionTarief.col.dagprijsHoog"),
      sortKey: "dagprijsHoog",
      cell: (r) =>
        r.dagprijsHoog != null ? (
          <span className="pension-tarief__prijs-hoog">{formatEuro(r.dagprijsHoog, locale)}</span>
        ) : (
          <span className="pension-tarief__missing">{t("pensionTarief.dagprijsHoog.missing")}</span>
        ),
    },
    {
      header: t("pensionTarief.volgorde.kolom"),
      // Omhoog en omlaag in plaats van slepen: DataTable geeft geen rij-eigenschappen om sleepgedrag
      // aan te haken, en die eraan toevoegen raakt elk scherm dat die tabel gebruikt. Twee knoppen
      // doen hetzelfde werk, werken met het toetsenbord en zijn voor te lezen.
      cell: (r) => (
        <span className="pt-greep">
          <GripVertical size={14} aria-hidden="true" className="pt-greep__icoon" />
          <button
            type="button"
            className="pt-greep__knop"
            data-testid={`volgorde-omhoog-${r.tariefId}`}
            aria-label={t("pensionTarief.volgorde.omhoog", { args: [r.verblijftype] })}
            onClick={() => verplaats(r, -1)}
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            className="pt-greep__knop"
            data-testid={`volgorde-omlaag-${r.tariefId}`}
            aria-label={t("pensionTarief.volgorde.omlaag", { args: [r.verblijftype] })}
            onClick={() => verplaats(r, 1)}
          >
            <ChevronDown size={14} />
          </button>
        </span>
      ),
    },
    {
      header: t("pensionTarief.col.status"),
      cell: (r) => (
        <label className="pt-status">
          <span className="fui-toggle">
            <input
              type="checkbox"
              role="switch"
              className="fui-toggle__input"
              data-testid={`tarief-actief-${r.tariefId}`}
              checked={r.actief}
              aria-label={t(r.actief ? "pensionTarief.status.actief" : "pensionTarief.status.inactief")}
              onChange={() => wisselActief(r)}
            />
            <span className="fui-toggle__track" aria-hidden="true"><span className="fui-toggle__thumb" /></span>
          </span>
          <span className="pt-status__tekst">
            {t(r.actief ? "pensionTarief.status.actief" : "pensionTarief.status.inactief")}
          </span>
        </label>
      ),
    },
    {
      header: t("pensionTarief.col.rassen"),
      cell: (r) => (
        <button
          type="button"
          className="pt-rassen__badge"
          data-testid={`rassen-${r.tariefId}`}
          onClick={() => setRassenVan(r)}
        >
          {t("pensionTarief.col.rassen.aantal", { args: [String(r.aantalRassen)] })}
          <ChevronRightIcon width={13} height={13} />
        </button>
      ),
    },
    {
      header: t("pensionTarief.col.geldigVanaf"),
      cell: (r) => (r.geldigVanaf ? formatDate(r.geldigVanaf, locale) : t("pensionTarief.none")),
    },
    {
      header: t("pensionTarief.col.laatstGewijzigd"),
      cell: (r) => (r.laatstGewijzigd ? formatDate(r.laatstGewijzigd, locale) : t("pensionTarief.none")),
    },
    {
      header: t("pensionTarief.col.acties"),
      cell: (r) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setBewerkRow(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="tarief-rijactie tarief-rijactie--verwijder"
            onClick={() => onDelete(r)}
            title={t("pensionTarief.action.delete")}
            aria-label={t("pensionTarief.action.delete")}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      {/* high-season banner — shown when the peildatum falls in an active hoogseizoen period */}
      {kpis?.hoogseizoenActief && (
        <div className="pension-tarief__banner" role="status" data-testid="hoogseizoen-banner">
          {t("pensionTarief.banner.hoogseizoen")}
        </div>
      )}


      <div className="asiel-tarief__toolbar">
        <VestigingFilter
          label={t("financieel.filter.vestiging")}
          opties={vestiging.opties}
          gekozen={vestiging.gekozen}
          onChange={vestiging.setGekozen}
        />
        <Field
          label={t("pensionTarief.search")}
          placeholder={t("pensionTarief.search")}
          value={zoek}
          onChange={(e) => {
            setZoek(e.target.value);
            setPage(0);
          }}
        />
        <SelectField
          label={t("pensionTarief.hoog.label")}
          value={hoogFilter}
          onChange={(e) => {
            setHoogFilter(e.target.value);
            setPage(0);
          }}
          options={[
            { value: "", label: t("pensionTarief.hoog.alle") },
            { value: t("pensionTarief.hoog.eigen"), label: t("pensionTarief.hoog.eigen") },
            { value: t("pensionTarief.hoog.gelijk"), label: t("pensionTarief.hoog.gelijk") },
            { value: t("pensionTarief.hoog.geen"), label: t("pensionTarief.hoog.geen") },
          ]}
        />
        <Field
          label={t("pensionTarief.peildatum")}
          type="date"
          value={peildatum}
          onChange={(e) => {
            setPeildatum(e.target.value);
            setPage(0);
          }}
        />
        <div className="asiel-tarief__toolbar-knoppen">
          {aiBeschikbaar && <AiHulpKnop onClick={() => setAiOpen(true)} />}
          <Button onClick={() => { setNieuwSeed(undefined); setNieuwOpen(true); }}>
            <PlusIcon width={16} height={16} /> {t("pensionTarief.action.add")}
          </Button>
        </div>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`pensionTarief.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("pensionTarief.load.error")}
        </div>
      ) : (
        <>
          {tegels.length > 0 && (
            <SoortTegels
              label={t("pensionTarief.col.diersoort")}
              gekozen={diersoort}
              onKies={(k) => { setDiersoort(k); setPage(0); }}
              tegels={tegels}
            />
          )}
          <div className="fawkes-catalogus__lijstkop">
            <span className="fawkes-muted">
              {filtered.length}{" "}{t(filtered.length === 1 ? "dierAanwezig.results.foundOne" : "dierAanwezig.results.found")}
            </span>
            <WeergaveKeuze
              weergave={weergave}
              onKies={kiesWeergave}
              labels={{
                groep: t("catalogus.weergave"),
                kaart: t("catalogus.weergave.kaart"),
                compact: t("catalogus.weergave.compact"),
                lijst: t("catalogus.weergave.lijst"),
              }}
            />
          </div>
          <CatalogusLijst
            columns={columns}
            rows={pageRows}
            getRowKey={(r) => String(r.tariefId)}
            empty={isLoading ? t("common.loading") : t("pensionTarief.empty")}
            weergave={weergave}
            sort={{ key: sortKey, dir: sortDir }}
            onSortChange={(next) => {
              setSortKey(next.key as typeof sortKey);
              setSortDir(next.dir);
              setPage(0);
            }}
          />
          {volgordeFout ? (
            <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
              {t("pensionTarief.volgorde.fout")}
            </div>
          ) : null}
          {/* De pagina's tellen hier vanaf 0 (de tabel snijdt zelf), de voetregel toont ze vanaf 1. */}
          <TablePager
            page={page + 1}
            pageCount={pageCount}
            total={filtered.length}
            from={filtered.length === 0 ? 0 : page * pageSize + 1}
            to={Math.min((page + 1) * pageSize, filtered.length)}
            pageSize={pageSize}
            onPage={(nummer) => setPage(nummer - 1)}
            onPageSize={(maat) => {
              setPageSize(maat);
              setPage(0);
            }}
            labels={{
              range: t("pager.range"),
              previous: t("pensionTarief.paging.prev"),
              next: t("pensionTarief.paging.next"),
              pageSize: t("pager.pageSize"),
              page: t("pager.page"),
            }}
          />
        </>
      )}

      {aiOpen && (
        <AiTariefHulp
          pad="pension-tarief/ai-suggestie"
          voorbeeld={t("tariefAi.voorbeeld.pension")}
          diersoorten={diersoorten}
          vandaag={todayIso()}
          onClose={() => setAiOpen(false)}
          onNieuw={(seed) => { setAiOpen(false); setNieuwSeed(seed); setNieuwOpen(true); }}
          onBewerk={(tariefId) => {
            const rij = alleRijen.find((r) => String(r.tariefId) === tariefId);
            if (rij) { setAiOpen(false); setBewerkRow(rij); }
          }}
          onVerwijder={(tariefId) => {
            const rij = alleRijen.find((r) => String(r.tariefId) === tariefId);
            if (rij) { setAiOpen(false); onDelete(rij); }
          }}
        />
      )}
      {nieuwOpen && (
        <NieuwPensionTariefDialog
          seed={nieuwSeed}
          onClose={() => setNieuwOpen(false)}
          onSaved={() => {
            setNieuwOpen(false);
            refreshAll();
          }}
        />
      )}

      {bewerkRow && (
        <TariefBewerkDialog
          row={bewerkRow}
          onClose={() => setBewerkRow(null)}
          onSaved={() => {
            setBewerkRow(null);
            refreshAll();
          }}
          onRassen={() => { setBewerkRow(null); setRassenVan(bewerkRow); }}
        />
      )}

      {rassenVan && (
        <RassenVenster
          tariefId={rassenVan.tariefId}
          tariefNaam={rassenVan.verblijftype}
          onSluit={() => setRassenVan(null)}
          onOpgeslagen={() => { setRassenVan(null); refreshAll(); }}
        />
      )}
    </div>
  );
}

// ── NieuwPensionTariefDialog (create head + first version) ────────────────────────────────────────
function NieuwPensionTariefDialog({ onClose, onSaved, seed }: {
  onClose: () => void;
  onSaved: () => void;
  /** Voorgevulde velden, bijvoorbeeld uit de AI-hulp. Alles blijft daarna gewoon te wijzigen. */
  seed?: TariefSeed;
}) {
  const t = useTranslate();
  const diersoorten = useDiersoortOpties();
  const [naam, setNaam] = useState(seed?.naam ?? "");
  const [diersoortId, setDiersoortId] = useState(seed?.diersoortId ?? "");
  const [omschrijving, setOmschrijving] = useState("");
  const [bedrag, setBedrag] = useState(seed?.bedrag ?? "");
  const [bedragHoog, setBedragHoog] = useState(seed?.bedragHoog ?? "");
  const [effectdatum, setEffectdatum] = useState(seed?.effectdatum ?? todayIso());
  // Een nieuw tarief heeft geen eerdere versie om BTW van over te nemen: hier moet hij gekozen worden.
  const btwCodes = useBtwCodes();
  const [btwCodeId, setBtwCodeId] = useState("");

  const [naamMissing, setNaamMissing] = useState(false);
  const [soortMissing, setSoortMissing] = useState(false);
  const [bedragMissing, setBedragMissing] = useState(false);
  const [btwMissing, setBtwMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    const prijs = num(bedrag);
    setNaamMissing(naam.trim() === "");
    setSoortMissing(diersoortId === "");
    setBedragMissing(prijs == null);
    setBtwMissing(btwCodeId === "");
    if (naam.trim() === "" || diersoortId === "" || prijs == null || btwCodeId === "") return;
    setNotice(null);

    const isPast = effectdatum !== "" && effectdatum < todayIso();
    mutate(
      {
        url: "pension-tarief",
        method: "post",
        values: {
          naam: naam.trim(),
          diersoortId: diersoortId === "" ? null : Number(diersoortId),
          omschrijving: omschrijving.trim() === "" ? null : omschrijving.trim(),
          bedrag: prijs,
          bedragHoog: num(bedragHoog),
          effectdatum: effectdatum === "" ? null : effectdatum,
          btwCodeId: Number(btwCodeId),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err, isPast)),
      },
    );
  };

  return (
    <Modal
      open
      title={t("pensionTarief.nieuw.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`pensionTarief.notice.${notice}`)}
          </div>
        )}
        <Field label={t("pensionTarief.field.naam")} value={naam} onChange={(e) => setNaam(e.target.value)} />
        {naamMissing && <div className="asiel-tarief__field-error">{t("pensionTarief.field.naam.required")}</div>}
        <SelectField
          label={t("pensionTarief.field.diersoort")}
          value={diersoortId}
          onChange={(e) => setDiersoortId(e.target.value)}
          options={[
            { value: "", label: t("pensionTarief.field.diersoort.placeholder") },
            ...diersoorten.map((d) => ({ value: String(d.id), label: d.label })),
          ]}
        />
        {soortMissing && (
          <div className="asiel-tarief__field-error">{t("pensionTarief.field.diersoort.required")}</div>
        )}
        <TextareaField
          label={t("pensionTarief.field.omschrijving")}
          value={omschrijving}
          rows={2}
          onChange={(e) => setOmschrijving(e.target.value)}
        />
        <Field
          label={t("pensionTarief.field.dagprijs")}
          value={bedrag}
          inputMode="decimal"
          onChange={(e) => setBedrag(e.target.value)}
        />
        {bedragMissing && (
          <div className="asiel-tarief__field-error">{t("pensionTarief.field.dagprijs.required")}</div>
        )}
        <Field
          label={t("pensionTarief.field.dagprijsHoog")}
          value={bedragHoog}
          inputMode="decimal"
          onChange={(e) => setBedragHoog(e.target.value)}
        />
        <Field
          label={t("pensionTarief.field.effectdatum")}
          type="date"
          value={effectdatum}
          onChange={(e) => setEffectdatum(e.target.value)}
        />
        <BtwKeuze
          value={btwCodeId}
          onChange={(v) => { setBtwCodeId(v); setBtwMissing(false); }}
          codes={btwCodes}
          missing={btwMissing}
          t={t}
        />
      </div>
    </Modal>
  );
}

// ── TariefBewerkDialog (the 4-section full pension editor) ─────────────────────────────────────────
/**
 * De diersoorten zoals de inboekwizard ze aanbiedt. Er is geen aparte tarief-feed voor soorten, en
 * twee lijsten die uit elkaar lopen is erger dan er een lenen. retry:false zodat een onbereikbare
 * feed het scherm niet blokkeert.
 */
function useDiersoortOpties() {
  const { data } = useCustom<{ id: number; label: string }[]>({
    url: "intake/reference/diersoorten",
    method: "get",
    queryOptions: { retry: false },
  });
  return useMemo(() => (Array.isArray(data?.data) ? data!.data : []), [data]);
}

// Section 1 Basisinformatie — naam/diersoort/omschrijving editable, vestiging read-only → PUT /{id}/basisinfo
// Section 2 Huidig tarief — the active laag/hoog day price (read-only)
// Section 3 Prijswijziging inplannen — one or more future TariefVersie rows → POST /{id}/prijswijziging
// Section 4 Tariefoverzicht — version history (GET /{id}/historie) with status badges; future versions deletable
interface GeplandeRow {
  effectdatum: string;
  bedrag: string;
  bedragHoog: string;
  reden: string;
  /** Verplicht; voorgevuld met de BTW van de lopende versie, want meestal wijzigt alleen het bedrag. */
  btwCodeId: string;
  /** Waar deze prijs op geboekt wordt; leeg laten mag en valt terug op wat er op het tarief staat. */
  grootboekRekeningId: string;
  kostenPlaatsId: string;
  projectCodeId: string;
}

function TariefBewerkDialog({
  row,
  onClose,
  onSaved,
  onRassen,
}: {
  row: PensionTariefRow;
  onClose: () => void;
  onSaved: () => void;
  /** Opent het rassenvenster; dit venster sluit dan, want ze gaan over hetzelfde tarief. */
  onRassen: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { naamVan } = useVestigingKeuze();
  const vestigingNaam = naamVan(row.vestigingId);
  const diersoorten = useDiersoortOpties();
  const [soortMissing, setSoortMissing] = useState(false);

  // ── Section 1: Basisinformatie ──
  const [naam, setNaam] = useState(row.verblijftype);
  const [diersoortId, setDiersoortId] = useState(row.diersoortId != null ? String(row.diersoortId) : "");
  const [omschrijving, setOmschrijving] = useState(row.omschrijving ?? "");
  const [basisNotice, setBasisNotice] = useState<SaveNotice>(null);
  const [basisSaved, setBasisSaved] = useState(false);
  const { mutate: saveBasis, isLoading: savingBasis } = useCustomMutation();

  const submitBasis = () => {
    setSoortMissing(diersoortId === "");
    if (naam.trim() === "" || diersoortId === "") {
      setBasisNotice("error");
      return;
    }
    setBasisNotice(null);
    setBasisSaved(false);
    saveBasis(
      {
        url: `pension-tarief/${row.tariefId}/basisinfo`,
        method: "put",
        values: {
          naam: naam.trim(),
          diersoortId: diersoortId === "" ? null : Number(diersoortId),
          omschrijving: omschrijving.trim() === "" ? null : omschrijving.trim(),
          version: row.version,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setBasisSaved(true);
          onSaved();
        },
        onError: (err) => setBasisNotice(statusToErr(err)),
      },
    );
  };

  // ── Section 4: Tariefoverzicht (history) ──
  const {
    data: histData,
    isLoading: histLoading,
    refetch: refetchHist,
  } = useCustom<TariefHistorieRegel[]>({
    url: `pension-tarief/${row.tariefId}/historie`,
    method: "get",
  });
  const historie = useMemo(() => (Array.isArray(histData?.data) ? histData!.data : []), [histData]);
  // Wat er nog aankomt hoort bij de knop waarmee je het inplant; wat geweest is staat eronder. Die twee
  // worden anders gelezen: het eerste controleer je en wijzig je nog, het tweede sla je er hooguit op na.
  const gepland = useMemo(() => historie.filter((h) => h.status === "GEPLAND"), [historie]);
  const verleden = useMemo(() => historie.filter((h) => h.status !== "GEPLAND"), [historie]);
  const btwCodes = useBtwCodes();
  const opties = useFinancieleOpties();
  const huidigeBtw = huidigeBtwUitHistorie(historie);
  // De boeking van de versie die nu loopt, om een nieuwe rij mee voor te vullen.
  const huidigeFinancieel = useMemo(() => {
    const lopend = historie.find((h) => h.status === "ACTIEF") ?? historie[0];
    return {
      grootboekRekeningId: lopend?.grootboekRekeningId != null ? String(lopend.grootboekRekeningId) : "",
      kostenPlaatsId: lopend?.kostenPlaatsId != null ? String(lopend.kostenPlaatsId) : "",
      projectCodeId: lopend?.projectCodeId != null ? String(lopend.projectCodeId) : "",
    };
  }, [historie]);

  // ── Section 3: Prijswijziging inplannen ──
  const [geplande, setGeplande] = useState<GeplandeRow[]>([]);
  const [prijsNotice, setPrijsNotice] = useState<SaveNotice>(null);
  const [btwMissing, setBtwMissing] = useState(false);
  const { mutate: planWijziging, isLoading: planning } = useCustomMutation();

  /** Een lege keuze is "niet gezet" en niet nul. */
  const idOf = (v: string) => (v.trim() === "" ? null : Number(v));

  const addRow = () =>
    setGeplande((g) => [
      ...g,
      {
        effectdatum: todayIso(), bedrag: "", bedragHoog: "", reden: "", btwCodeId: huidigeBtw,
        // Voorgevuld met wat er nu geldt: meestal wijzigt alleen het bedrag en hoort de boeking gelijk
        // te blijven. Leeg laten mag en valt terug op wat er op het tarief staat.
        grootboekRekeningId: huidigeFinancieel.grootboekRekeningId,
        kostenPlaatsId: huidigeFinancieel.kostenPlaatsId,
        projectCodeId: huidigeFinancieel.projectCodeId,
      },
    ]);
  const removeRow = (idx: number) => setGeplande((g) => g.filter((_, i) => i !== idx));
  const setRow = (idx: number, patch: Partial<GeplandeRow>) =>
    setGeplande((g) => g.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  // Een rij die al openstond voordat de historie binnen was, krijgt de lopende BTW alsnog — maar een
  // rij waar de gebruiker zelf iets koos, laten we staan.
  useEffect(() => {
    if (huidigeBtw === "") return;
    setGeplande((g) => g.map((r) => (r.btwCodeId === "" ? { ...r, btwCodeId: huidigeBtw } : r)));
  }, [huidigeBtw]);

  const submitPrijswijziging = () => {
    const valid = geplande.filter((r) => r.effectdatum !== "" && num(r.bedrag) != null);
    if (valid.length === 0) {
      setPrijsNotice("error");
      return;
    }
    // BTW is verplicht per versie; niet versturen zolang er een rij zonder BTW tussen zit.
    if (valid.some((r) => r.btwCodeId === "")) {
      setBtwMissing(true);
      return;
    }
    setBtwMissing(false);
    setPrijsNotice(null);
    const anyPast = valid.some((r) => r.effectdatum < todayIso());

    const payload =
      valid.length === 1
        ? {
            bedrag: num(valid[0].bedrag),
            bedragHoog: num(valid[0].bedragHoog),
            effectdatum: valid[0].effectdatum,
            reden: valid[0].reden.trim() === "" ? null : valid[0].reden.trim(),
            btwCodeId: Number(valid[0].btwCodeId),
            grootboekRekeningId: idOf(valid[0].grootboekRekeningId),
            kostenPlaatsId: idOf(valid[0].kostenPlaatsId),
            projectCodeId: idOf(valid[0].projectCodeId),
          }
        : {
            geplandeWijzigingen: valid.map((r) => ({
              effectdatum: r.effectdatum,
              bedrag: num(r.bedrag),
              bedragHoog: num(r.bedragHoog),
              btwCodeId: Number(r.btwCodeId),
              reden: r.reden.trim() === "" ? null : r.reden.trim(),
              grootboekRekeningId: idOf(r.grootboekRekeningId),
              kostenPlaatsId: idOf(r.kostenPlaatsId),
              projectCodeId: idOf(r.projectCodeId),
            })),
          };

    planWijziging(
      {
        url: `pension-tarief/${row.tariefId}/prijswijziging`,
        method: "post",
        values: payload,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setGeplande([]);
          refetchHist();
          onSaved();
        },
        onError: (err) => setPrijsNotice(statusToErr(err, anyPast)),
      },
    );
  };

  /**
   * Een tarief uit gebruik nemen, of weer in gebruik.
   *
   * <p>Hier stond verwijderen. Dat endpoint laat de kop staan maar haalt zijn prijsversies weg -- een
   * omweg van vroeger, toen er geen actief-kolom was. Die is er nu, en een tarief dat op eerdere
   * boekingen is toegepast hoort zijn prijshistorie te houden: zonder die versies is niet meer na te
   * gaan tegen welke prijs er toen geboekt is. Uit gebruik nemen haalt het uit de keuzelijsten en laat
   * de administratie heel.
   *
   * <p>Verwijderen blijft bestaan als rijactie in de lijst, voor een tarief dat per ongeluk is
   * aangemaakt en nergens op zit.
   */
  const { mutate: zetActief, isLoading: actiefBezig } = useCustomMutation();
  const [actiefFout, setActiefFout] = useState(false);
  const wisselGebruik = () => {
    const uit = row.actief;
    if (uit && !globalThis.confirm(t("pensionTarief.actief.bevestig", { args: [row.verblijftype] }))) {
      return;
    }
    setActiefFout(false);
    zetActief(
      {
        url: `pension-tarief/${row.tariefId}/basisinfo`,
        method: "put",
        values: {
          naam: row.verblijftype,
          diersoortId: row.diersoortId,
          omschrijving: row.omschrijving,
          actief: !row.actief,
          version: row.version,
        },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => { onSaved(); onClose(); }, onError: () => setActiefFout(true) },
    );
  };

  const histColumns: Column<TariefHistorieRegel>[] = [
    { header: t("pensionTarief.hist.vanaf"), cell: (r) => formatDate(r.effectdatum, locale) },
    { header: t("pensionTarief.hist.tot"), cell: (r) => formatDate(r.einddatum, locale) },
    {
      header: t("pensionTarief.hist.bedrag"),
      align: "right",
      cell: (r) => formatEuro(r.bedrag, locale),
    },
    {
      header: t("pensionTarief.hist.bedragHoog"),
      align: "right",
      cell: (r) => (r.bedragHoog != null ? formatEuro(r.bedragHoog, locale) : "—"),
    },
    { header: t("btw.kolom"), align: "right", cell: (r) => btwWeergave(btwCodes, r.btwCodeId) },
    // De reden stond hier, maar draagt bij elke bestaande versie de tekst van de migratie
    // ("Fase-1 initiële seed vanuit pension_prijs_categorie_prijs"). Dat is rommel in een overzicht dat
    // moet laten zien wat er gold. Waar een prijs op geboekt werd is dat wel.
    { header: t("pensionTarief.hist.grootboek"), cell: (r) => labelVan(opties.grootboekRekeningen, r.grootboekRekeningId) },
    { header: t("pensionTarief.hist.kostenplaats"), cell: (r) => labelVan(opties.kostenPlaatsen, r.kostenPlaatsId) },
    { header: t("pensionTarief.hist.projectcode"), cell: (r) => labelVan(opties.projectCodes, r.projectCodeId) },
    {
      header: t("pensionTarief.hist.status"),
      cell: (r) => (
        <StatusBadge tone={historieTone(r.status)}>{t(`pensionTarief.hist.status.${r.status}`)}</StatusBadge>
      ),
    },
  ];

  return (
    <Modal
      open
      size="wide"
      title={t("pensionTarief.bewerk.title", { args: [row.verblijftype] })}
      onClose={onClose}
      footer={
        <div className="pt-venstervoet">
          <button
            type="button"
            className={"pt-venstervoet__uitgebruik" + (row.actief ? "" : " pt-venstervoet__uitgebruik--terug")}
            onClick={wisselGebruik}
            disabled={actiefBezig}
          >
            {row.actief ? <PowerOffIcon size={15} /> : <PowerIcon size={15} />}
            {t(row.actief ? "pensionTarief.actief.uit" : "pensionTarief.actief.aan")}
          </button>
          {actiefFout ? (
            <span className="pt-venstervoet__fout" role="alert">{t("pensionTarief.actief.fout")}</span>
          ) : null}
          <span className="pt-venstervoet__vulling" />
          <Button variant="secondary" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      }
    >
      <div className="asiel-tarief__form">
        {/* ── Section 1: Basisinformatie ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("pensionTarief.section.basis")}</legend>
          {basisNotice && (
            <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
              {t(`pensionTarief.notice.${basisNotice}`)}
            </div>
          )}
          {basisSaved && (
            <div className="asiel-tarief__notice asiel-tarief__notice--success" role="status">
              {t("pensionTarief.basis.saved")}
            </div>
          )}
          <Field label={t("pensionTarief.field.naam")} value={naam} onChange={(e) => setNaam(e.target.value)} />
          <SelectField
            label={t("pensionTarief.field.diersoort")}
            value={diersoortId}
            onChange={(e) => setDiersoortId(e.target.value)}
            options={[
              { value: "", label: t("pensionTarief.field.diersoort.placeholder") },
              ...diersoorten.map((d) => ({ value: String(d.id), label: d.label })),
            ]}
          />
          {soortMissing && (
            <div className="asiel-tarief__field-error">{t("pensionTarief.field.diersoort.required")}</div>
          )}
          <TextareaField
            label={t("pensionTarief.field.omschrijving")}
            value={omschrijving}
            rows={2}
            onChange={(e) => setOmschrijving(e.target.value)}
          />
          <Field
            label={t("pensionTarief.field.vestiging")}
            value={vestigingNaam ?? t("pensionTarief.vestiging.org")}
            readOnly
            disabled
          />
          <div>
            <Button onClick={submitBasis} disabled={savingBasis}>
              {t("pensionTarief.basis.save")}
            </Button>
          </div>
        </fieldset>

        {/* ── Section 2: Huidig tarief ── */}
        <fieldset className="asiel-tarief__section">
          <legend>
            {row.geldigVanaf
              ? t("pensionTarief.section.huidigVanaf", { args: [formatDate(row.geldigVanaf, locale)] })
              : t("pensionTarief.section.huidig")}
          </legend>
          <div className="pt-seizoen">
            <div className="pt-seizoen__kaart pt-seizoen__kaart--laag">
              <span className="pt-seizoen__icoon" aria-hidden="true"><Snowflake size={18} /></span>
              <span className="pt-seizoen__tekst">
                <span className="pt-seizoen__naam">{t("pensionTarief.seizoen.laag")}</span>
                <span className="pt-seizoen__bedrag">
                  {row.dagprijs != null ? formatEuro(row.dagprijs, locale) : t("pensionTarief.none")}
                  <span className="pt-seizoen__eenheid">{t("pensionTarief.seizoen.perDag")}</span>
                </span>
              </span>
            </div>
            <div className="pt-seizoen__kaart pt-seizoen__kaart--hoog">
              <span className="pt-seizoen__icoon" aria-hidden="true"><Sun size={18} /></span>
              <span className="pt-seizoen__tekst">
                <span className="pt-seizoen__naam">{t("pensionTarief.seizoen.hoog")}</span>
                <span className="pt-seizoen__bedrag">
                  {row.dagprijsHoog != null
                    ? formatEuro(row.dagprijsHoog, locale)
                    : t("pensionTarief.geenSeizoensprijs")}
                  {row.dagprijsHoog != null
                    ? <span className="pt-seizoen__eenheid">{t("pensionTarief.seizoen.perDag")}</span>
                    : null}
                </span>
              </span>
            </div>
          </div>
        </fieldset>

        {/* ── Gekoppelde rassen ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("pensionTarief.rassen.sectie")}</legend>
          <div className="pt-rassen__sectie">
            <div>
              <span className="pt-rassen__aantal">
                {t("pensionTarief.col.rassen.aantal", { args: [String(row.aantalRassen)] })}
              </span>
              <p className="pt-rassen__uitleg">{t("pensionTarief.rassen.uitleg")}</p>
            </div>
            <Button variant="secondary" onClick={onRassen}>{t("pensionTarief.rassen.beheren")}</Button>
          </div>
        </fieldset>

        {/* ── Section 3: Prijswijziging inplannen ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("pensionTarief.section.gepland")}</legend>
          {/* Wat er al ingepland staat, bij de knop waarmee je er iets aan toevoegt. */}
          <IndeelbareTabel tabelKey="pension-tarief-overview-1"
            columns={histColumns}
            rows={gepland}
            getRowKey={(r) => String(r.versieId)}
            empty={histLoading ? t("common.loading") : t("pensionTarief.gepland.empty")}
          />
          {prijsNotice && (
            <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
              {t(`pensionTarief.notice.${prijsNotice}`)}
            </div>
          )}
          <p className="asiel-tarief__cond-empty">{t("pensionTarief.prijswijziging.hint")}</p>
          {btwMissing && (
            <div className="asiel-tarief__field-error" role="alert">{t("btw.verplicht")}</div>
          )}
          {geplande.map((r, idx) => (
            <div className="pension-tarief__plan-row pension-tarief__plan-row--btw" key={idx} data-testid="plan-row">
              <Field
                label={t("pensionTarief.field.effectdatum")}
                type="date"
                value={r.effectdatum}
                onChange={(e) => setRow(idx, { effectdatum: e.target.value })}
              />
              <Field
                label={t("pensionTarief.field.dagprijs")}
                value={r.bedrag}
                inputMode="decimal"
                onChange={(e) => setRow(idx, { bedrag: e.target.value })}
              />
              <Field
                label={t("pensionTarief.field.dagprijsHoog")}
                value={r.bedragHoog}
                inputMode="decimal"
                onChange={(e) => setRow(idx, { bedragHoog: e.target.value })}
              />
              <SelectField
                label={t("pensionTarief.hist.grootboek")}
                value={r.grootboekRekeningId}
                onChange={(e) => setRow(idx, { grootboekRekeningId: e.target.value })}
                options={keuzes(opties.grootboekRekeningen, t("pensionTarief.financieel.geen"))}
              />
              <SelectField
                label={t("pensionTarief.hist.kostenplaats")}
                value={r.kostenPlaatsId}
                onChange={(e) => setRow(idx, { kostenPlaatsId: e.target.value })}
                options={keuzes(opties.kostenPlaatsen, t("pensionTarief.financieel.geen"))}
              />
              <SelectField
                label={t("pensionTarief.hist.projectcode")}
                value={r.projectCodeId}
                onChange={(e) => setRow(idx, { projectCodeId: e.target.value })}
                options={keuzes(opties.projectCodes, t("pensionTarief.financieel.geen"))}
              />
              <BtwKeuze
                value={r.btwCodeId}
                onChange={(v) => { setRow(idx, { btwCodeId: v }); setBtwMissing(false); }}
                codes={btwCodes}
                missing={false}
                t={t}
              />
              <Field
                label={t("pensionTarief.field.reden")}
                value={r.reden}
                onChange={(e) => setRow(idx, { reden: e.target.value })}
              />
              <button
                type="button"
                className="asiel-tarief__link asiel-tarief__cond-remove"
                onClick={() => removeRow(idx)}
                aria-label={t("pensionTarief.prijswijziging.remove")}
              >
                ×
              </button>
            </div>
          ))}
          <div className="asiel-tarief__row-actions">
            <Button variant="secondary" onClick={addRow}>
              {t("pensionTarief.prijswijziging.add")}
            </Button>
            {geplande.length > 0 && (
              <Button onClick={submitPrijswijziging} disabled={planning}>
                {t("pensionTarief.prijswijziging.submit")}
              </Button>
            )}
          </div>
        </fieldset>

        {/* ── Historie: wat geweest is, zonder wat nog aankomt ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("pensionTarief.section.historie")}</legend>
          <IndeelbareTabel tabelKey="pension-tarief-overview-2"
            columns={histColumns}
            rows={verleden}
            getRowKey={(r) => String(r.versieId)}
            empty={histLoading ? t("common.loading") : t("pensionTarief.hist.empty")}
          />
        </fieldset>
      </div>
    </Modal>
  );
}

// ── Hoogseizoen tab ──────────────────────────────────────────────────────────────────────────────
function HoogseizoenTab() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<HoogseizoenPeriode[]>({
    url: "pension-tarief/hoogseizoen",
    method: "get",
  });
  const rows = useMemo(() => (Array.isArray(data?.data) ? data!.data : []), [data]);

  // inline create form
  const [naam, setNaam] = useState("");
  const [vanaf, setVanaf] = useState("");
  const [tm, setTm] = useState("");
  const [formNotice, setFormNotice] = useState<SaveNotice>(null);
  const { mutate: create, isLoading: creating } = useCustomMutation();
  const { mutate: del } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const submit = () => {
    if (naam.trim() === "" || vanaf === "" || tm === "") {
      setFormNotice("error");
      return;
    }
    setFormNotice(null);
    create(
      {
        url: "pension-tarief/hoogseizoen",
        method: "post",
        values: { naam: naam.trim(), vestigingId: null, geldigVanaf: vanaf, geldigTm: tm },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setNaam("");
          setVanaf("");
          setTm("");
          refetch();
        },
        onError: (err) => setFormNotice(statusToErr(err)),
      },
    );
  };

  const onDelete = (p: HoogseizoenPeriode) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("pensionTarief.hoogseizoen.delete.confirm", { args: [p.naam] }))) return;
    del(
      {
        url: `pension-tarief/hoogseizoen/${p.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  const columns: Column<HoogseizoenPeriode>[] = [
    { header: t("pensionTarief.hoogseizoen.col.naam"), cell: (p) => p.naam },
    { header: t("pensionTarief.hoogseizoen.col.vanaf"), cell: (p) => formatDate(p.geldigVanaf, locale) },
    { header: t("pensionTarief.hoogseizoen.col.tm"), cell: (p) => formatDate(p.geldigTm, locale) },
    {
      header: t("pensionTarief.hoogseizoen.col.scope"),
      cell: (p) =>
        p.vestigingId != null ? String(p.vestigingId) : t("pensionTarief.hoogseizoen.scope.org"),
    },
    {
      header: t("pensionTarief.col.acties"),
      cell: (p) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => onDelete(p)}
            title={t("pensionTarief.action.delete")}
            aria-label={t("pensionTarief.action.delete")}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      {/* inline create form */}
      <fieldset className="asiel-tarief__section">
        <legend>{t("pensionTarief.hoogseizoen.new.title")}</legend>
        {formNotice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`pensionTarief.notice.${formNotice}`)}
          </div>
        )}
        <div className="pension-tarief__plan-row">
          <Field
            label={t("pensionTarief.hoogseizoen.field.naam")}
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
          />
          <Field
            label={t("pensionTarief.hoogseizoen.field.vanaf")}
            type="date"
            value={vanaf}
            onChange={(e) => setVanaf(e.target.value)}
          />
          <Field
            label={t("pensionTarief.hoogseizoen.field.tm")}
            type="date"
            value={tm}
            onChange={(e) => setTm(e.target.value)}
          />
        </div>
        <div>
          <Button onClick={submit} disabled={creating}>
            {t("pensionTarief.hoogseizoen.add")}
          </Button>
        </div>
      </fieldset>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`pensionTarief.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("pensionTarief.hoogseizoen.load.error")}
        </div>
      ) : (
        <IndeelbareTabel tabelKey="pension-tarief-overview-3"
          columns={columns}
          rows={rows}
          getRowKey={(p) => String(p.id)}
          empty={isLoading ? t("common.loading") : t("pensionTarief.hoogseizoen.empty")}
        />
      )}
    </div>
  );
}

// ── Aanbetaling tab ──────────────────────────────────────────────────────────────────────────────
interface AanbetalingInstellingen {
  vestigingId: number | null;
  vestigingCode: string | null;
  percentage: number;
  herinneringNaDagen: number;
  annulerenNaDagen: number;
}

type AanbetalingForm = { percentage: string; herinneringNaDagen: string; annulerenNaDagen: string };

/**
 * Hoeveel een klant vooraf betaalt bij een pensionreservering, en wanneer hij een herinnering krijgt en de
 * reservering vervalt. Voor de vestiging waarin je werkt; de dagen tellen vanaf de bevestiging. Wijzigen
 * vraagt beheerrechten (de api geeft anders 403, en die melding staat dan boven het formulier).
 */
function AanbetalingTab() {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom<AanbetalingInstellingen>({
    url: "pension/aanbetaling/instellingen",
    method: "get",
  });
  const instellingen = data?.data;
  const [form, setForm] = useState<AanbetalingForm | null>(null);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const { mutate, isLoading: saving } = useCustomMutation<AanbetalingInstellingen>();

  const waarden: AanbetalingForm | null =
    form ??
    (instellingen
      ? {
          percentage: String(instellingen.percentage),
          herinneringNaDagen: String(instellingen.herinneringNaDagen),
          annulerenNaDagen: String(instellingen.annulerenNaDagen),
        }
      : null);

  if (isError) {
    return (
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t("pensionTarief.aanbetaling.load.error")}
        </div>
      </div>
    );
  }
  if (isLoading || !waarden) {
    return (
      <div className="asiel-tarief__panel">
        <p>{t("common.loading")}</p>
      </div>
    );
  }

  const zet = (veld: keyof AanbetalingForm) => (e: { target: { value: string } }) => {
    setOpgeslagen(false);
    setForm({ ...waarden, [veld]: e.target.value });
  };
  // Een leeg veld zet die instelling terug op de standaardwaarde.
  const getal = (s: string) => (s.trim() === "" ? null : Number(s));

  const submit = () => {
    const percentage = getal(waarden.percentage);
    const herinnering = getal(waarden.herinneringNaDagen);
    const annuleren = getal(waarden.annulerenNaDagen);
    const heel = (n: number | null, max: number) => n == null || (Number.isInteger(n) && n >= 0 && n <= max);
    if (
      !heel(percentage, 100) ||
      !heel(herinnering, 365) ||
      !heel(annuleren, 365) ||
      (herinnering != null && annuleren != null && annuleren < herinnering)
    ) {
      setNotice("error");
      return;
    }
    setNotice(null);
    mutate(
      {
        url: "pension/aanbetaling/instellingen",
        method: "put",
        values: { percentage, herinneringNaDagen: herinnering, annulerenNaDagen: annuleren },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setForm(null);
          setOpgeslagen(true);
          refetch();
        },
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <div className="asiel-tarief__panel">
      <fieldset className="asiel-tarief__section">
        <legend>{t("pensionTarief.aanbetaling.title")}</legend>
        <p>{t("pensionTarief.aanbetaling.intro")}</p>
        {instellingen?.vestigingCode && (
          <p>{t("pensionTarief.aanbetaling.scope", { args: [instellingen.vestigingCode] })}</p>
        )}
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`pensionTarief.notice.${notice}`)}
          </div>
        )}
        {opgeslagen && (
          <div className="asiel-tarief__notice" role="status">
            {t("pensionTarief.aanbetaling.saved")}
          </div>
        )}
        <div className="pension-tarief__plan-row">
          <Field
            label={t("pensionTarief.aanbetaling.field.percentage")}
            type="number"
            value={waarden.percentage}
            onChange={zet("percentage")}
          />
          <Field
            label={t("pensionTarief.aanbetaling.field.herinnering")}
            type="number"
            value={waarden.herinneringNaDagen}
            onChange={zet("herinneringNaDagen")}
          />
          <Field
            label={t("pensionTarief.aanbetaling.field.annuleren")}
            type="number"
            value={waarden.annulerenNaDagen}
            onChange={zet("annulerenNaDagen")}
          />
        </div>
        <div>
          <Button onClick={submit} disabled={saving}>
            {t("pensionTarief.aanbetaling.save")}
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

// ── Kortingen tab ────────────────────────────────────────────────────────────────────────────────
function KortingenTab() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<KortingRegel[]>({
    url: "pension-tarief/kortingen",
    method: "get",
  });
  const rows = useMemo(() => (Array.isArray(data?.data) ? data!.data : []), [data]);

  // AI availability probe — same approach as the Asiel AI button. With the
  // `fawkes.producten.ai-features.enabled` flag off the controller bean is absent and ANY method
  // (incl. this GET) gets a 404 → hide the button. With it on the route exists (POST-only) so a GET
  // gets a 405 → button shown. retry:false so the 404 surfaces immediately.
  const aiUnavailable = !useAiBeschikbaar("pension-tarief/kortingen/ai-suggestie");

  const [dialogRule, setDialogRule] = useState<KortingRegel | "new" | null>(null);
  const [aiPrefill, setAiPrefill] = useState<Partial<KortingRegel> | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const { mutate: del } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const onDelete = (r: KortingRegel) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("pensionTarief.korting.delete.confirm", { args: [r.naam] }))) return;
    del(
      {
        url: `pension-tarief/kortingen/${r.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  const waardeCell = (r: KortingRegel): string => {
    if (r.kortingstype === "PERCENTAGE") return `${r.waarde}%`;
    return formatEuro(r.waarde, locale);
  };

  const columns: Column<KortingRegel>[] = [
    { header: t("pensionTarief.korting.col.naam"), cell: (r) => r.naam },
    {
      header: t("pensionTarief.korting.col.type"),
      cell: (r) => (r.kortingstype ? t(`pensionTarief.korting.type.${r.kortingstype}`) : "—"),
    },
    { header: t("pensionTarief.korting.col.waarde"), align: "right", cell: waardeCell },
    {
      header: t("pensionTarief.korting.col.toepassing"),
      cell: (r) => (r.toepassing ? t(`pensionTarief.korting.toepassing.${r.toepassing}`) : "—"),
    },
    {
      header: t("pensionTarief.korting.col.actief"),
      cell: (r) => (
        <StatusBadge tone={r.actief ? "success" : "neutral"}>
          {r.actief ? t("pensionTarief.korting.actief.yes") : t("pensionTarief.korting.actief.no")}
        </StatusBadge>
      ),
    },
    {
      header: t("pensionTarief.col.acties"),
      cell: (r) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setDialogRule(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="tarief-rijactie tarief-rijactie--verwijder"
            onClick={() => onDelete(r)}
            title={t("pensionTarief.action.delete")}
            aria-label={t("pensionTarief.action.delete")}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <Button
          onClick={() => {
            setAiPrefill(null);
            setDialogRule("new");
          }}
        >
          {t("pensionTarief.korting.add")}
        </Button>
        {!aiUnavailable && (
          <Button variant="secondary" onClick={() => setAiOpen(true)}>
            {t("pensionTarief.korting.ai")}
          </Button>
        )}
        <span className="asiel-tarief__toolbar-spacer" />
        <Button variant="secondary" onClick={() => refetch()}>
          {t("pensionTarief.action.refresh")}
        </Button>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`pensionTarief.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("pensionTarief.korting.load.error")}
        </div>
      ) : (
        <IndeelbareTabel tabelKey="pension-tarief-overview-4"
          columns={columns}
          rows={rows}
          getRowKey={(r) => String(r.id)}
          empty={isLoading ? t("common.loading") : t("pensionTarief.korting.empty")}
        />
      )}

      {dialogRule !== null && (
        <KortingDialog
          rule={dialogRule === "new" ? null : dialogRule}
          prefill={dialogRule === "new" ? aiPrefill : null}
          onClose={() => {
            setDialogRule(null);
            setAiPrefill(null);
          }}
          onSaved={() => {
            setDialogRule(null);
            setAiPrefill(null);
            refetch();
          }}
        />
      )}

      {aiOpen && (
        <AiKortingDialog
          onClose={() => setAiOpen(false)}
          onAdopt={(draft) => {
            setAiOpen(false);
            setAiPrefill(draftToKorting(draft));
            setDialogRule("new");
          }}
        />
      )}
    </div>
  );
}

// ── KortingDialog (the ALS/EN/DAN/OP builder) ─────────────────────────────────────────────────────
function KortingDialog({
  rule,
  prefill,
  onClose,
  onSaved,
}: {
  rule: KortingRegel | null;
  prefill: Partial<KortingRegel> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const seed = rule ?? prefill ?? null;

  const [naam, setNaam] = useState(seed?.naam ?? "");
  const [conditieJson, setConditieJson] = useState(seed?.conditieJson ?? "");
  const [kortingstype, setKortingstype] = useState(seed?.kortingstype ?? "PERCENTAGE");
  const [waarde, setWaarde] = useState(seed?.waarde != null ? String(seed.waarde) : "");
  const [toepassing, setToepassing] = useState(seed?.toepassing ?? "ALLE_DIEREN");
  const [geldigVanaf, setGeldigVanaf] = useState(seed?.geldigVanaf ?? todayIso());
  const [geldigTm, setGeldigTm] = useState(seed?.geldigTm ?? "");
  const [actief, setActief] = useState(seed?.actief ?? true);

  const [naamMissing, setNaamMissing] = useState(false);
  const [waardeMissing, setWaardeMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    const w = num(waarde);
    setNaamMissing(naam.trim() === "");
    setWaardeMissing(w == null);
    if (naam.trim() === "" || w == null) return;
    setNotice(null);

    const values = {
      naam: naam.trim(),
      vestigingId: null,
      conditieJson: conditieJson.trim(),
      kortingstype,
      waarde: w,
      toepassing,
      geldigVanaf: geldigVanaf === "" ? null : geldigVanaf,
      geldigTm: geldigTm === "" ? null : geldigTm,
      actief,
      ...(rule ? { version: rule.version } : {}),
    };

    mutate(
      {
        url: rule ? `pension-tarief/kortingen/${rule.id}` : "pension-tarief/kortingen",
        method: rule ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={rule ? t("pensionTarief.korting.dialog.edit.title") : t("pensionTarief.korting.dialog.new.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`pensionTarief.notice.${notice}`)}
          </div>
        )}

        <Field label={t("pensionTarief.korting.field.naam")} value={naam} onChange={(e) => setNaam(e.target.value)} />
        {naamMissing && (
          <div className="asiel-tarief__field-error">{t("pensionTarief.korting.field.naam.required")}</div>
        )}

        {/* ALS / EN — the condition (vocabulary string; AI or hand-authored) */}
        <TextareaField
          label={t("pensionTarief.korting.field.conditie")}
          value={conditieJson}
          rows={2}
          onChange={(e) => setConditieJson(e.target.value)}
        />

        {/* DAN — kortingstype + waarde */}
        <SelectField
          label={t("pensionTarief.korting.field.type")}
          value={kortingstype}
          onChange={(e) => setKortingstype(e.target.value)}
          options={KORTING_TYPES.map((k) => ({ value: k, label: t(`pensionTarief.korting.type.${k}`) }))}
        />
        <Field
          label={t("pensionTarief.korting.field.waarde")}
          value={waarde}
          inputMode="decimal"
          onChange={(e) => setWaarde(e.target.value)}
        />
        {waardeMissing && (
          <div className="asiel-tarief__field-error">{t("pensionTarief.korting.field.waarde.required")}</div>
        )}

        {/* OP — toepassing */}
        <SelectField
          label={t("pensionTarief.korting.field.toepassing")}
          value={toepassing}
          onChange={(e) => setToepassing(e.target.value)}
          options={TOEPASSINGEN.map((a) => ({ value: a, label: t(`pensionTarief.korting.toepassing.${a}`) }))}
        />

        <Field
          label={t("pensionTarief.korting.field.geldigVanaf")}
          type="date"
          value={geldigVanaf}
          onChange={(e) => setGeldigVanaf(e.target.value)}
        />
        <Field
          label={t("pensionTarief.korting.field.geldigTm")}
          type="date"
          value={geldigTm}
          onChange={(e) => setGeldigTm(e.target.value)}
        />
        <CheckboxField
          label={t("pensionTarief.korting.field.actief")}
          checked={actief}
          onChange={(e) => setActief(e.target.checked)}
        />
      </div>
    </Modal>
  );
}

// ── AiKortingDialog ──────────────────────────────────────────────────────────────────────────────
function AiKortingDialog({
  onClose,
  onAdopt,
}: {
  onClose: () => void;
  onAdopt: (draft: KortingAiDraft) => void;
}) {
  const t = useTranslate();
  const [tekst, setTekst] = useState("");
  const [draft, setDraft] = useState<KortingAiDraft | null>(null);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading } = useCustomMutation();

  const generate = () => {
    setNotice(null);
    mutate(
      {
        url: "pension-tarief/kortingen/ai-suggestie",
        method: "post",
        values: { tekst },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setDraft(res?.data as KortingAiDraft),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={t("pensionTarief.korting.ai.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          {draft ? (
            <Button onClick={() => onAdopt(draft)}>{t("pensionTarief.korting.ai.adopt")}</Button>
          ) : (
            <Button onClick={generate} disabled={isLoading || tekst.trim() === ""}>
              {t("pensionTarief.korting.ai.generate")}
            </Button>
          )}
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`pensionTarief.notice.${notice}`)}
          </div>
        )}
        <TextareaField
          label={t("pensionTarief.korting.ai.prompt")}
          value={tekst}
          rows={4}
          onChange={(e) => setTekst(e.target.value)}
        />
        {draft && (
          <div className="asiel-tarief__ai-preview" data-testid="korting-ai-preview">
            <strong>{draft.suggestieNaam}</strong>
            <p>{draft.previewTekst}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function draftToKorting(draft: KortingAiDraft): Partial<KortingRegel> {
  return {
    naam: draft.suggestieNaam,
    conditieJson: draft.conditieJson,
    kortingstype: draft.kortingstype,
    waarde: draft.waarde,
    toepassing: draft.toepassing,
  };
}
